import { useEffect, useMemo, useState } from "react";
import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";
import "./GoNoGo.css";

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalStimuli: 20,
    stimulusDuration: 1300,
    interval: 900,
    goProbability: 0.8,
  },
  medium: {
    label: "Media",
    totalStimuli: 30,
    stimulusDuration: 900,
    interval: 650,
    goProbability: 0.7,
  },
  hard: {
    label: "Difícil",
    totalStimuli: 40,
    stimulusDuration: 650,
    interval: 450,
    goProbability: 0.6,
  },
};

function createStimulus(config) {
  const isGo = Math.random() < config.goProbability;

  return {
    type: isGo ? "go" : "nogo",
    symbol: isGo ? "🟢" : "🔴",
  };
}

export default function GoNoGo() {
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [config, setConfig] = useState(null);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [stimulusIndex, setStimulusIndex] = useState(0);
  const [stimulus, setStimulus] = useState(null);
  const [stimulusStartTime, setStimulusStartTime] = useState(null);
  const [hasResponded, setHasResponded] = useState(false);

  const [hits, setHits] = useState(0);
  const [omissions, setOmissions] = useState(0);
  const [commissions, setCommissions] = useState(0);
  const [reactionTimes, setReactionTimes] = useState([]);

  const totalStimuli = config?.totalStimuli || 0;

  const progress = useMemo(() => {
    if (!totalStimuli) return 0;
    return Math.round((stimulusIndex / totalStimuli) * 100);
  }, [stimulusIndex, totalStimuli]);

  const averageReactionTime =
    reactionTimes.length > 0
      ? Math.round(
          reactionTimes.reduce((acc, item) => acc + item, 0) /
            reactionTimes.length
        )
      : 0;

  const accuracy =
    totalStimuli > 0
      ? Math.round(((hits + (totalStimuli - hits - omissions - commissions)) / totalStimuli) * 100)
      : 0;

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors: omissions + commissions,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (difficulty = selectedDifficulty || "easy") => {
    const nextConfig = GAME_CONFIG[difficulty] || GAME_CONFIG.easy;

    setSelectedDifficulty(difficulty);
    setConfig(nextConfig);

    setStarted(true);
    setFinished(false);

    setStimulusIndex(0);
    setStimulus(null);
    setStimulusStartTime(null);
    setHasResponded(false);

    setHits(0);
    setOmissions(0);
    setCommissions(0);
    setReactionTimes([]);
  };

  const finishGame = () => {
    setStarted(false);
    setFinished(true);
    setStimulus(null);
  };

  useEffect(() => {
    if (!started || !config) return;

    if (stimulusIndex >= config.totalStimuli) {
      finishGame();
      return;
    }

    const nextStimulus = createStimulus(config);
    setStimulus(nextStimulus);
    setStimulusStartTime(Date.now());
    setHasResponded(false);

    const stimulusTimer = setTimeout(() => {
      if (nextStimulus.type === "go" && !hasResponded) {
        setOmissions((prev) => prev + 1);
      }

      setStimulus(null);

      const nextTimer = setTimeout(() => {
        setStimulusIndex((prev) => prev + 1);
      }, config.interval);

      return () => clearTimeout(nextTimer);
    }, config.stimulusDuration);

    return () => clearTimeout(stimulusTimer);
  }, [started, stimulusIndex, config, hasResponded]);

  const handlePress = () => {
    if (!started || !stimulus || hasResponded || !config) return;

    setHasResponded(true);

    if (stimulus.type === "go") {
      const reactionTime = Date.now() - stimulusStartTime;

      setHits((prev) => prev + 1);
      setReactionTimes((prev) => [...prev, reactionTime]);
    } else {
      setCommissions((prev) => prev + 1);
    }

    setStimulus(null);

    setTimeout(() => {
      setStimulusIndex((prev) => prev + 1);
    }, config.interval);
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);
    setStarted(false);
    setFinished(false);
    setStimulus(null);
  };

  if (!selectedDifficulty && !started && !finished) {
    return (
      <GameLauncher
        title="Go / No-Go"
        description="Pulsa cuando aparezca el estímulo verde y evita pulsar cuando aparezca el rojo."
        areas={["Control inhibitorio", "Atención", "Impulsividad"]}
        defaultDifficulty="easy"
        onStart={startGame}
      />
    );
  }

  return (
    <div className="gonogo-page">
      <div className="gonogo-card">
        <header className="gonogo-header">
          <div>
            <h1>Go / No-Go</h1>
            <p>
              Dificultad: <strong>{config?.label}</strong>
            </p>
          </div>

          <button type="button" onClick={() => startGame(selectedDifficulty)}>
            Reiniciar
          </button>
        </header>

        {started && (
          <>
            <GameProgress
              label="Estímulo"
              current={Math.min(stimulusIndex + 1, totalStimuli)}
              total={totalStimuli}
              progress={progress}
            />

            <section className="gonogo-instructions">
              <span>🟢 Pulsa</span>
              <span>🔴 No pulses</span>
            </section>

            <section className="stimulus-zone">
              {stimulus ? (
                <div className="stimulus">{stimulus.symbol}</div>
              ) : (
                <div className="stimulus-placeholder">Prepárate...</div>
              )}
            </section>

            <button type="button" className="press-button" onClick={handlePress}>
              PULSAR
            </button>

            <section className="gonogo-live-score">
              <span>Aciertos: {hits}</span>
              <span>Omisiones: {omissions}</span>
              <span>Impulsivos: {commissions}</span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                { label: "Aciertos", value: hits },
                { label: "Omisiones", value: omissions },
                { label: "Impulsivos", value: commissions },
                { label: "Precisión", value: `${accuracy}%` },
                { label: "Tiempo medio", value: `${averageReactionTime} ms` },
              ]}
              onRepeat={() => startGame(selectedDifficulty)}
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation interpretation={interpretation} />
          </>
        )}
      </div>
    </div>
  );
}